// @flow
import * as React from 'react';
import {
    Box, Button, Stack, Text,
} from 'grommet';
import { connectAPI, connectWS, withRouter } from 'speed-js-react';
import ReactTable from 'react-table';
import { get, isEqual, isEmpty } from 'lodash';
import memoizeOne from 'memoize-one';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import 'react-table/react-table.css';
import { Refresh, Task as TaskIcon } from 'grommet-icons/es6';
import styled from 'styled-components';
import type { WebSocketClient } from 'speed-js-core';
import { FormattedMessage } from 'react-intl';
import RowNumberColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/RowNumberColumn';
import RecordCount from 'speed-js-react-web/src/containers/grommet2/tables/RecordCount';
import FilterForm from '../../tables/FilterForm';
import FilterSidebarLayer from '../../tables/FilterSidebarLayer';
import ColumnSettingsForm from '../../tables/ColumnSettingsForm';
import Pagination from './Pagination';
import Context from './Context';
import SearchField from '../../../components/fields/SearchField';
import CheckBoxColumn from '../../../components/grommet2/tables/columns/CheckBoxColumn';

type Props = {
    resourceName: string;
    resourceMethod: string;
    resourceArguments?: Array<any>;
    sortColumn: string,
    sortOrder: string,
    columns: Array<{
        Header: React.Node,
        accessor?: string,
        [string]: any,
    }>,
    expandRow: (row: Object) => React.Node,
    additionalQuery?: Object,
    loadingSpinner?: Component,
    setLoading?: Function,
    filter: Component,
    tableId: string,
    columnSettingsFormComponent: React.ComponentType<Object>,
    BottomPagination: Component,
    TopPagination: Component,
    striped?: boolean,
    highlight?: boolean,
    additionalActions: React.Node,
    filterExcludeFields?: Array<string>,
    filterUnsetPaths?: Array<string>,
    refreshIcon?: React.Node,
    headerComponent: React.ComponentType,
    headerChildren?: React.Node,
    onDataRetrieval: Function,
    excludeColumns?: Array<number>,
    ws: WebSocketClient,
    refreshCount?: number,
    onReloadTableData?: number,
    includeRowNumberColumn?: number,
    defaultPageSize?: number,
    showSearchField?: boolean,
    isLoading: boolean,
    customFilterSchema: Object,
    getTableRowProps: Function,
    showCheckboxButton?: boolean,
    // used to get the uuid property from data as key in checkboxes
    checkboxesKey: string,
    checkboxCustomFormComponent: React.FC
}

type CombinedProps = Props & {
    getData: (query: Object) => Promise<Object>,
    data: Array<Object>,
    count: number,
    offset: number,
    limit: number,
    totalCount?: number,
    location?: Object,
    history?: Object,
};

type State = {
    excludedColumns: Array<number>,
    openedBy: Array<Object>,
    rowCount: number,
    // show or hide checkboxes
    showCheckboxes: boolean,
    // keep element open - custom checkboxes form - dynamic key
    keepOpenCheckboxesElement: boolean,
    markAllChecked: boolean,
    selectedRows: Array<number>,
}

const StyledTable = styled(ReactTable)`
    .rt-td, .rt-th {
        white-space: normal;
        text-overflow: initial;
        outline-color: transparent;
    },
    .rt-th.rt-resizable-header {
        border-right: 1px solid #c1c3c5;
        margin-top: 5px;
        margin-bottom: 5px;
    },
    .ReactTable{
        border: none;
        margin: 10px;
    },
`;

export const VerticalSeparator = styled(Box)`
    border-right: 1px solid #c1c3c5;
`;

@connectWS
@connectAPI((apiClient: ApiClient, props: CombinedProps) => ({
    getData: {
        send: (data: Object) => {
            const {
                additionalQuery, resourceName, resourceMethod, resourceArguments, setLoading,
            } = props;

            // Scroll to the top of the table
            document.querySelector('.rt-thead').scrollIntoView({ block: 'end' });

            if (typeof setLoading === typeof Function) setLoading(true);

            const args = resourceArguments ? [...resourceArguments] : [];
            if (additionalQuery) {
                args.push({
                    ...additionalQuery, ...data,
                });
            } else {
                args.push(data);
            }

            const resource = get(apiClient.resources, resourceName);

            /* TODO: This is a monkey patch to allow having named filters as resourceArguments
            This example is how it normally works, where the first argument is the parent object id,
            and the second is the filter
            [
             0: '116',
             1: {limit: 10, offset: 0, filter: {name: 'test'}
            ]

            This is how we need it to work for documents, since we're always filtering by entity
            [
             0: {entity: 123-141asd-123}
             1: {limit: 10, offset: 0, filter: {name: 'test'}
            ]

            So below if the resource is documents, we're going to merge the first two arguments into one object
            that looks like this:
            [
             0: {entity: 123-141asd-123, limit: 10, offset: 0, filter: {name: 'test'}
            ]
            */
            if (resourceName === 'document') {
                const resourceMethodArgs = args.reduce((acc, arg) => {
                    if (typeof arg === 'object') {
                        return { ...acc, ...arg };
                    }
                    return [...acc, arg];
                }, []);
                return resource[resourceMethod](resourceMethodArgs);
            }
            return resource[resourceMethod](...args);
        },
        success: (response: {results: Array<Object>}, data: Object) => {
            const {
                results, count, offset, limit, totalCount,
            } = response;
            const { setLoading, onDataRetrieval } = props;

            if (typeof setLoading === typeof Function) setLoading(false);

            if (onDataRetrieval) {
                onDataRetrieval(results);
            }

            return {
                data: results,
                count,
                offset,
                pageSize: limit,
                totalCount,
            };
        },
    },
}), true)
@withRouter
class Table extends React.PureComponent<CombinedProps, State> {
    query: Object;

    storage = window.localStorage;

    static defaultProps: Object;

    constructor(props: CombinedProps) {
        super(props);

        this.parseSearchParamsToQuery = memoizeOne(this.parseSearchParamsToQuery);

        const {
            offset, tableId, excludeColumns, location,
        } = props;

        const searchParams = this.parseSearchParamsToQuery(location.search);

        this.state = {
            showCheckboxes: false,
            markAllChecked: false,
            selectedRows: [],
            keepOpenCheckboxesElement: false,
            excludedColumns: this.storage.getItem(`tables:${tableId}`)
                ? JSON.parse(this.storage.getItem(`tables:${tableId}`)) : excludeColumns,
            openedBy: {},
            ordering: [],
            rowCount: searchParams.limit || (this.storage.getItem(`rc${tableId}`)
                ? JSON.parse(this.storage.getItem(`rc${tableId}`)) : props.defaultPageSize),
        };

        const sessionFilterValues = JSON.parse(sessionStorage.getItem(`tables:${tableId}`) || '{}');

        this.query = {
            limit: searchParams.limit || this.storage.getItem(`rc${tableId}`)
                ? JSON.parse(this.storage.getItem(`rc${tableId}`)) : props.defaultPageSize,
            offset: searchParams.offset || offset,
            ...sessionFilterValues,
        };

        this.getColumns = memoizeOne(this.getColumns);
        this.getSourceColumns = memoizeOne(this.getSourceColumns);
        this.setCheckboxes = memoizeOne(this.setCheckboxes);
    }

    componentDidMount(): void {
        this.orderTable();
        this.setRowCount();

        this.subscribeToViewersGroup();
    }

    componentDidUpdate(prevProps: CombinedProps): void {
        const {
            resourceName, resourceMethod, tableId, additionalQuery, resourceArguments, excludeColumns,
            location,
        } = this.props;

        if (prevProps.resourceName !== resourceName || prevProps.resourceMethod !== resourceMethod
            || !isEqual(prevProps.additionalQuery, additionalQuery)
            || !isEqual(prevProps.resourceArguments, resourceArguments)
            || prevProps.tableId !== tableId) {
            const { rowCount } = this.state;

            const searchParams = this.parseSearchParamsToQuery(location.search);

            this.query = {
                limit: rowCount,
                offset: searchParams.offset || 0,
            };

            const filterSessionStorage = sessionStorage.getItem(`tables:${tableId}`);
            if (filterSessionStorage) {
                Object.assign(this.query, JSON.parse(filterSessionStorage));
            }

            this.setRowCount();
            this.orderTable();
        }
        if (prevProps.tableId !== tableId) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                excludedColumns: this.storage.getItem(`tables:${tableId}`)
                    ? JSON.parse(this.storage.getItem(`tables:${tableId}`)) : excludeColumns,
            });
        }

        if (this.props.data !== prevProps.data) {
            let { offset } = this.props;
            const { count } = this.props;

            if (offset > count) {
                const pages = Math.floor(count / this.query.limit);
                offset = pages * this.query.limit;

                const searchParams = this.parseSearchParamsToQuery(location.search);
                if (searchParams.offset !== offset) {
                    this.setQueryAndGetData({ offset });
                }
            }

            this.updateMarkAll();
        }
    }

    componentWillUnmount() {
        this.unsubscribeFromViewersGroup();
    }

    onManageCheckboxColumnHandler = (hideCheckboxes: boolean = false) => {
        // when filter apply button is clicked or search field value is changed - we call this method with
        // 'true' value of param 'hideCheckboxes'
        this.setState((prevState) => ({
            selectedRows: [],
            keepOpenCheckboxesElement: false,
            markAllChecked: false,
            showCheckboxes: hideCheckboxes ? false : !prevState.showCheckboxes,
        }));
    }

    onRowSelectHandler = (value: number) => {
        const { selectedRows } = this.state;
        const { data, checkboxesKey } = this.props;
        const isCurrRowSelected = selectedRows.some((row) => row === value);
        const uuids = data.map((x) => x[checkboxesKey]);

        if (isCurrRowSelected) {
            this.setState((prevState) => ({
                selectedRows: prevState.selectedRows.filter((row) => row !== value),
                markAllChecked: false,
            }));
        } else {
            this.setState((prevState) => ({
                selectedRows: [...prevState.selectedRows, value],
                // after first select on checkbox, custom form element will open.
                // This state variable keep it open, till the 'show checkboxes button' is clicked again
                keepOpenCheckboxesElement: true,
                /* check if every element in data is in selectedRows array - its
                used to update markAllChecked */
                markAllChecked: isEqual(uuids, [...selectedRows, value]),
            }));
        }
    }


    updateMarkAll = () => {
        const { checkboxesKey, data } = this.props;
        const { selectedRows } = this.state;

        /* check if every element in data is in selectedRows array - its
            used to update markAllChecked */
        const containsAllElements = isEqual(data
            .map((el) => el[checkboxesKey]), selectedRows);

        if (containsAllElements) {
            this.setState({ markAllChecked: true });
        } else {
            this.setState({ markAllChecked: false });
        }
    };

    onMarkAllSelectHandler = () => {
        const { data, checkboxesKey } = this.props;
        const uuids = data.map((x) => x[checkboxesKey]);
        const { markAllChecked } = this.state;

        if (markAllChecked) {
            this.setState((prevState) => ({
                markAllChecked: !prevState.markAllChecked,
                selectedRows: prevState.selectedRows.filter((row) => !uuids.includes(row)),
            }));
        } else {
            this.setState((prevState) => ({
                markAllChecked: !prevState.markAllChecked,
                keepOpenCheckboxesElement: true,
                selectedRows: [
                    ...prevState.selectedRows,
                    ...uuids.filter((uuid) => !prevState.selectedRows.includes(uuid))],
            }));
        }
    }

    parseSearchParamsToQuery = (search: string): Object => {
        const params = new URLSearchParams(search);
        const query = {};
        params.forEach((value, key) => {
            if (typeof query[key] !== 'undefined') {
                if (!Array.isArray(query[key])) {
                    query[key] = [query[key]];
                }
                query[key].push(value);
            } else {
                query[key] = value;
            }
        });
        if (typeof query.limit !== 'undefined') {
            query.limit = Number.parseInt(query.limit, 10);
        }
        if (typeof query.offset !== 'undefined') {
            query.offset = Number.parseInt(query.offset, 10);
        }

        return query;
    }

    setQueryAndGetData = (query: Object) => {
        const { location, history, getData } = this.props;
        this.query = {
            ...this.query,
            ...query,
        };
        const changeSearch = typeof query.offset !== 'undefined' || typeof query.limit !== 'undefined';

        if (changeSearch) {
            const params = new URLSearchParams(location.query);
            params.set('limit', this.query.limit);
            params.set('offset', this.query.offset);
            history.replace(`${location.pathname}?${params.toString()}`);
        }

        getData(this.query);
    }

    orderTable = () => {
        const { tableId } = this.props;
        let ordering = sessionStorage.getItem(`${tableId}-ordering`);
        if (ordering) {
            ordering = JSON.parse(ordering);
            this.setState({
                ordering: ordering.sorted,
            });
            this.query = {
                ...this.query,
                ordering: ordering.ordering.join(','),
            };
        }
    }

    setRowCount = () => {
        const { tableId, location } = this.props;
        const rc = this.storage.getItem(`rc${tableId}`);
        const searchParams = this.parseSearchParamsToQuery(location.search);
        if (rc || searchParams.limit) {
            this.setState({
                rowCount: searchParams.limit || JSON.parse(rc),
            });
            this.query = {
                ...this.query,
                limit: searchParams.limit || JSON.parse(rc),
            };
        }
    }

    setCheckboxes = (selectedRows: Array<number>, markAllChecked: boolean) => (
        {
            Header: () => (
                <CheckBoxColumn
                    checked={markAllChecked}
                    value={markAllChecked}
                    onChange={this.onMarkAllSelectHandler}
                    disable={false}
                />
            ),
            accessor: '',
            maxWidth: 40,
            Cell: ({ value }) => {
                const { checkboxesKey } = this.props;
                const id = value[checkboxesKey];
                return (
                    <CheckBoxColumn
                        checked={selectedRows.some((row) => row === id)}
                        onChange={this.onRowSelectHandler}
                        value={id}
                        disable={false}
                    />
                );
            },
            sortable: false,
        }
    );

    getSourceColumns = (
        cols: Array<{ Header: React.Node, accessor?: string, [string]: any}>,
        includeRowNumberColumn: boolean,
    ): Array<{ Header: React.Node, accessor?: string, [string]: any}> => {
        let columns = cols;

        if (includeRowNumberColumn) {
            columns = [{
                Header: '#',
                accessor: '',
                maxWidth: 40,
                Cell: RowNumberColumn,
                sortable: false,
            }].concat(columns);
        }

        return columns;
    }

    getColumns = (
        cols: Array<{ Header: React.Node, accessor?: string, [string]: any}>,
        excluded: Array<number>,
        openedBy: Array<Object>,
    ): Array<{ Header: React.Node, accessor?: string, [string]: any}> => {
        cols.filter((col) => col.Cell && col.Cell.name === 'ActionsColumn').forEach((column) => {
            column.sortable = false;
        });

        // Exclude columns
        const colsWithoutExcluded = cols.filter((c, idx) => !excluded.includes(idx));
        // Apply opened by to the viewer action
        colsWithoutExcluded.every((col) => {
            if (col.actions) {
                const colViewer = col.actions.find((object) => object.type === 'viewer');
                if (colViewer) {
                    colViewer.data = openedBy;
                    return false;
                }
            }
            return true;
        });
        return colsWithoutExcluded;
    };

    getPaginationData = () => {
        const { count, offset } = this.props;

        const { rowCount } = this.state;

        return {
            pages: Math.ceil((count / rowCount) || 1),
            page: (offset / rowCount) || 0,
        };
    };

    handleSortedChange = (sorted: Array<{id: string, desc: boolean}>, column, shiftKey) => {
        const { tableId } = this.props;
        const ordering = sorted.map((col) => {
            const orderBy = column.orderBy || col.id || '';
            let r = orderBy.replace('.', '__');
            if (col.desc) {
                r = `-${r}`;
            }
            return r;
        });

        sessionStorage.setItem(`${tableId}-ordering`, JSON.stringify({
            ordering, sorted,
        }));
        this.setState((prevState) => ({
            ...prevState,
            ordering: sorted,
        }));
        this.setQueryAndGetData({
            ordering: ordering.join(','),
        });
    };

    handlePageChange = (page: number) => {
        const { rowCount } = this.state;

        this.setQueryAndGetData({
            offset: page * rowCount,
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        this.setQueryAndGetData({
            limit: pageSize,
            offset: 0,
        });
    };

    handleFiltersSchemaLoad = (values: Object) => {
        const { getData, tableId } = this.props;

        const filterSessionStorage = sessionStorage.getItem(`tables:${tableId}`);

        if (values && (!filterSessionStorage || filterSessionStorage === '{}')) {
            // Override the filters kept in session storage with the initial values from the filters schema
            this.query = {
                ...this.query,
                offset: 0,
            };
            Object.keys(values).forEach((key) => {
                this.query[key] = values[key];
            });

            const currentValueObj = {};
            Object.entries(values).forEach(([key, value]) => {
                if (value) {
                    currentValueObj[key] = value;
                }
            });

            sessionStorage.setItem(`tables:${tableId}`, JSON.stringify(currentValueObj));
        }

        getData(this.query);
    };

    handleFiltersSubmit = (values: Object, formik: Object) => {
        const { getData, tableId } = this.props;

        if (values) {
            this.query = {
                ...this.query,
                offset: 0,
            };
            Object.keys(values).forEach((key) => {
                this.query[key] = values[key];
            });
            getData(this.query).then(() => {
                formik.setSubmitting(false);
            });

            const currentValueObj = {};
            Object.entries(values).forEach(([key, value]) => {
                if (value) {
                    currentValueObj[key] = value;
                }
            });

            sessionStorage.setItem(`tables:${tableId}`, JSON.stringify(currentValueObj));

            // clear selected rows and hide checkboxes, custom checkbox form, etc...
            this.onManageCheckboxColumnHandler(true);
        }
    };

    handleColumnSettingsSubmit = (ec: Array<number>, rc: number) => {
        const { tableId } = this.props;
        this.setState({ excludedColumns: ec, rowCount: rc });
        if (ec.length > 0) {
            this.storage.setItem(`tables:${tableId}`, JSON.stringify(ec));
        } else {
            this.storage.setItem(`tables:${tableId}`, JSON.stringify([]));
        }
        this.storage.setItem(`rc${tableId}`, JSON.stringify(rc));
        this.handlePageSizeChange(rc);
    };

    handleSearchSubmit = (value: string) => {
        const { getData } = this.props;

        if (value) {
            this.query = {
                ...this.query,
                offset: 0,
                search: value,
            };
            getData(this.query);
        } else {
            const newQuery = { ...this.query };
            // We need to delete it since the API won't accept empty string for a search param
            delete newQuery.search;
            this.query = { ...newQuery };
            getData(this.query);
        }

        // clear selected rows and hide checkboxes, custom checkbox form, etc...
        this.onManageCheckboxColumnHandler(true);
    };

    reloadData = (callback?: Function) => {
        const { getData } = this.props;
        getData(this.query).then(() => {
            if (callback) {
                callback();
            }
        });
    };

    getViewerGroupName = () => {
        const { columns } = this.props;
        let viewerGroupName;

        columns.every((col) => {
            if (col.actions) {
                const colViewer = col.actions.find((object) => object.type === 'viewer');
                if (colViewer) {
                    viewerGroupName = colViewer.groupName;
                    return false;
                }
            }
            return true;
        });
        return viewerGroupName;
    }

    subscribeToViewersGroup(): void {
        const { ws } = this.props;
        const viewerGroupName = this.getViewerGroupName();
        if (viewerGroupName) {
            ws.subscribe({
                [viewerGroupName]: (data: Object) => {
                    this.setState({
                        openedBy: {
                            ...data.opened_by,
                        },
                    });
                },
            });
        }
    }

    unsubscribeFromViewersGroup(): void {
        const { ws } = this.props;
        const viewerGroupName = this.getViewerGroupName();
        if (viewerGroupName) {
            ws.unSubscribe([viewerGroupName]);
        }
    }

    render() {
        const {
            data, columns, resourceName, resourceMethod, resourceArguments, offset, limit, count,
            expandRow, loadingSpinner, filter: Filter, BottomPagination, TopPagination, striped, highlight,
            tableId, additionalActions, filterExcludeFields, refreshIcon,
            headerComponent: Header, headerChildren, filterUnsetPaths, refreshCount, onReloadTableData, additionalQuery,
            columnSettingsFormComponent, excludeColumns: defaultExcludedColumns, defaultPageSize, showSearchField,
            isLoading, totalCount, customFilterSchema, getTableRowProps, showCheckboxButton,
            checkboxCustomFormComponent, includeRowNumberColumn,
        } = this.props;

        const CheckboxCustomFormComponent = checkboxCustomFormComponent;

        const {
            excludedColumns, openedBy, ordering, rowCount, showCheckboxes, selectedRows,
            keepOpenCheckboxesElement, markAllChecked,
        } = this.state;
        const selectedRowsLength = selectedRows.length;

        let tableClassName = '';
        if (striped) {
            tableClassName += ' -striped';
        }
        if (highlight) {
            tableClassName += ' -highlight';
        }

        /* separated funcs for checkboxes and source columns, because of memoize
           if showCheckboxes is true, then we add checkboxes column, plus source columns,
           otherwise we add only source columns */
        const sourceColumns = showCheckboxes ? [
            this.setCheckboxes(selectedRows, markAllChecked)]
            .concat(this.getSourceColumns(columns, false))
            : this.getSourceColumns(columns, includeRowNumberColumn);

        const ColumnFormComponent = columnSettingsFormComponent || ColumnSettingsForm;

        return (
            <Context.Provider value={this}>
                {loadingSpinner}
                <Header direction="row" justify="between">
                    {headerChildren
                        ? (
                            <Box direction="row">
                                {headerChildren}
                                <RecordCount
                                    from={offset}
                                    rowCount={Number(rowCount)}
                                    count={count}
                                    total={totalCount}
                                    changePage={this.handlePageChange}
                                />
                            </Box>
                        ) : (
                            <RecordCount
                                from={offset}
                                rowCount={Number(rowCount)}
                                count={count}
                                total={totalCount}
                                changePage={this.handlePageChange}
                            />
                        )
                    }
                    { /* Place for action which (possibly) affect all columns -> bulk edit, delete etc. */}
                    {TopPagination && (
                        <Box width="10%" justify="center">
                            <TopPagination
                                limit={limit}
                                offset={offset}
                                pageSize={rowCount}
                                count={count}
                                // onPageSizeChange={this.handlePageSizeChange}
                                onPageChange={this.handlePageChange}
                            />
                        </Box>
                    )}

                    { showSearchField && (
                        <Box align="center" direction="row" justify="center" width="40%" style={{ maxWidth: '800px' }}>
                            <SearchField searchIndex="Search..." onSubmit={this.handleSearchSubmit} />
                        </Box>
                    )}

                    <Box direction="row" justify="end">
                        {additionalActions}
                        {additionalActions && (
                            <VerticalSeparator
                                margin={{ vertical: 'small', left: 'small', right: 'small' }}
                            />
                        )}
                        {showCheckboxButton && (
                            <Button
                                pad={{ bottom: 'small' }}
                                justify="evenly"
                                onClick={() => this.onManageCheckboxColumnHandler()}
                                hoverIndicator="light-1"
                                style={{ backgroundColor: showCheckboxes ? '#F8F8F8' : '' }}
                            >
                                <Box pad={{ vertical: '10px', horizontal: 'small' }}>
                                    <Stack
                                        anchor="top-right"
                                    >
                                        <Box>
                                            {<TaskIcon />}
                                        </Box>
                                        {(selectedRowsLength !== 0 || keepOpenCheckboxesElement) && (
                                            <Box
                                                background="red"
                                                pad={{ horizontal: '4px' }}
                                                round
                                                style={{ position: 'relative', top: '-5px', right: '-5px' }}
                                            >
                                                <Text weight="bold" color="white" size="10px">
                                                    {selectedRowsLength}
                                                </Text>
                                            </Box>
                                        )}
                                    </Stack>
                                </Box>
                            </Button>
                        )}
                        <Button
                            pad={{ bottom: 'small' }}
                            justify="evenly"
                            onClick={() => this.reloadData(onReloadTableData)}
                            hoverIndicator="light-1"
                        >
                            <Box pad={{ vertical: '10px', horizontal: 'small' }}>
                                <Stack
                                    anchor="top-left"
                                >
                                    <Box>
                                        {refreshIcon || <Refresh />}
                                    </Box>
                                    {(refreshCount !== 0) && (
                                        <Box
                                            background="red"
                                            pad={{ horizontal: '4px' }}
                                            round
                                            style={{ position: 'relative', top: '-5px', left: '-5px' }}
                                        >
                                            <Text weight="bold" color="white" size="10px">{refreshCount}</Text>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        </Button>
                        <FilterForm
                            resourceName={resourceName}
                            resourceMethod={resourceMethod}
                            resourceArguments={resourceArguments}
                            additionalQuery={additionalQuery}
                            excludeFields={filterExcludeFields}
                            unsetPaths={filterUnsetPaths}
                            onFiltersSchemaLoad={this.handleFiltersSchemaLoad}
                            onSubmit={this.handleFiltersSubmit}
                            storageKey={`tables:${tableId}`}
                            customSchema={customFilterSchema}
                        >
                            {(props: Object) => {
                                if (props.schema && !isEmpty(props.schema.fields)) {
                                    const key = props.schema.fields.reduce((a, c) => a + c[0]);
                                    props.storageKey = `tables:${tableId}`;
                                    if (Filter) {
                                        return <Filter key={key} {...props} tableId={tableId} />;
                                    }
                                    return (
                                        <FilterSidebarLayer
                                            key={JSON.stringify(props.schema.fields)}
                                            tableId={tableId}
                                            {...props}
                                        />
                                    );
                                }
                                return null;
                            }}
                        </FilterForm>
                        <ColumnFormComponent
                            onSubmit={this.handleColumnSettingsSubmit}
                            columns={sourceColumns}
                            initialExcludedColumns={excludedColumns}
                            defaultExcludedColumns={defaultExcludedColumns}
                            tableId={tableId}
                            numberOfRows={rowCount}
                            defaultNumberOfRows={defaultPageSize}
                        />
                        {keepOpenCheckboxesElement && (
                            <CheckboxCustomFormComponent
                                reloadData={this.reloadData}
                                closeCheckboxColumn={
                                    this.onManageCheckboxColumnHandler
                                }
                                selectedRows={selectedRows}
                            />
                        )}
                    </Box>
                </Header>
                <StyledTable
                    sorted={ordering}
                    className={tableClassName}
                    showPagination={false}
                    data={data}
                    columns={this.getColumns(sourceColumns, excludedColumns, openedBy)}
                    pageSize={rowCount}
                    manual
                    onSortedChange={this.handleSortedChange}
                    minRows={0}
                    {...this.getPaginationData()}
                    SubComponent={expandRow}
                    // onPageSizeChange={this.handlePageSizeChange}
                    onPageChange={this.handlePageChange}
                    NoDataComponent={() => (
                        <div style={{
                            background: '#f8f8f8',
                            paddingTop: 30,
                            textAlign: 'center',
                        }}
                        >
                            <FormattedMessage
                                id="no-data-in-table"
                                defaultMessage="No Data"
                            />
                        </div>
                    )}
                    loading={isLoading}
                    // We already have the spinner on the page, this text is not needed
                    loadingText=""
                    getTrProps={getTableRowProps}
                />
                {!isEmpty(data) && (
                    <BottomPagination
                        limit={limit}
                        offset={offset}
                        pageSize={rowCount}
                        count={count}
                        // onPageSizeChange={this.handlePageSizeChange}
                        onPageChange={this.handlePageChange}
                    />
                )}
            </Context.Provider>
        );
    }
}

Table.defaultProps = {
    offset: 0,
    additionalQuery: undefined,
    resourceArguments: undefined,
    resourceMethod: 'list',
    loadingSpinner: undefined,
    setLoading: undefined,
    BottomPagination: Pagination,
    striped: false,
    highlight: false,
    filterExcludeFields: [],
    filterUnsetPaths: [],
    refreshIcon: undefined,
    headerComponent: Box,
    headerChildren: undefined,
    onDataRetrieval: undefined,
    excludeColumns: [],
    ws: {},
    refreshCount: 0,
    onReloadTableData: undefined,
    includeRowNumberColumn: true,
    defaultPageSize: 100,
    showSearchField: false,
    totalCount: null,
    location: {},
    history: {},
    getTableRowProps: undefined,
    showCheckboxButton: false,
    checkboxesKey: 'uuid',
};

export default Table;
